import {
  ArrowDown,
  ArrowUp,
  Camera,
  Circle,
  CircleDot,
  EyeOff,
  FilePlus2,
  Layers,
  Link2,
  Link2Off,
  RefreshCcw,
  type LucideIcon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { UnitEvent } from '$lib/api/types';
import { formatDateLong, formatKr } from '$lib/lib/formatters';

// Map every backend EventType variant to an icon + tone. Backend can introduce
// new event kinds at any time (PR #79's `listing_reconciled`, PR #83's
// strict-link audit events, future ones); we crashed on `listing_reconciled`
// because the lookup returned undefined. Defensive lookup below covers any
// kind we forgot to add explicitly.
const ICONS: Partial<Record<UnitEvent['kind'], LucideIcon>> = {
  listed: FilePlus2,
  new_listing: FilePlus2,
  price_change: CircleDot,
  delisted: EyeOff,
  relisted: FilePlus2,
  bbr_change: Layers,
  photo_update: Camera,
  attribute_change: Layers,
  unit_linked: Link2,
  unit_unlinked: Link2Off,
  listing_reconciled: RefreshCcw,
  cross_site_price_divergence: CircleDot,
  cross_site_new_listing: FilePlus2
};

const TONE: Partial<Record<UnitEvent['kind'], string>> = {
  listed: 'text-positive',
  new_listing: 'text-positive',
  price_change: 'text-accent',
  delisted: 'text-fg-subtle',
  relisted: 'text-positive',
  bbr_change: 'text-info',
  photo_update: 'text-fg-muted',
  attribute_change: 'text-info',
  unit_linked: 'text-positive',
  unit_unlinked: 'text-fg-subtle',
  listing_reconciled: 'text-fg-muted',
  cross_site_price_divergence: 'text-warning',
  cross_site_new_listing: 'text-positive'
};

const FALLBACK_ICON: LucideIcon = Circle;
const FALLBACK_TONE = 'text-fg-muted';

function priceArrow(from: number, to: number) {
  if (to > from) return <ArrowUp size={11} className="text-warning" />;
  if (to < from) return <ArrowDown size={11} className="text-positive" />;
  return null;
}

function describe(event: UnitEvent, t: TFunction): React.ReactNode {
  switch (event.kind) {
    case 'listed':
      return t('events.listed');
    case 'price_change': {
      const from = Number(event.delta?.from ?? 0);
      const to = Number(event.delta?.to ?? 0);
      return (
        <span className="flex flex-wrap items-center gap-1">
          <span>{t('events.rent')}</span>
          <span className="num text-fg-muted">{formatKr(from)}</span>
          {priceArrow(from, to)}
          <span className="num text-fg">{formatKr(to)}</span>
        </span>
      );
    }
    case 'delisted':
      return event.note ?? t('events.removedFromPortal');
    case 'relisted':
      return t('events.relisted');
    case 'bbr_change':
      return (
        <span>
          {t('events.bbrPrefix')} <span className="text-fg-muted">{event.delta?.field}</span>{' '}
          <span className="num text-fg-muted">{String(event.delta?.from ?? '—')}</span>
          {' → '}
          <span className="num text-fg">{String(event.delta?.to ?? '—')}</span>
        </span>
      );
    case 'photo_update':
      return t('events.photoUpdate');
    default:
      return event.note ?? '';
  }
}

export interface EventLogTimelineProps {
  events: UnitEvent[];
}

export function EventLogTimeline({ events }: EventLogTimelineProps) {
  const { t } = useTranslation();
  return (
    <section
      data-testid="event-log-timeline"
      aria-label={t('events.panelAriaLabel')}
      className="flex min-h-0 flex-col overflow-hidden rounded-sm border border-rule bg-surface"
    >
      <header className="flex items-center justify-between border-b border-rule bg-surface-2 px-3 py-2">
        <h2 className="text-xs font-semibold text-fg">{t('events.title')}</h2>
        <span className="text-2xs text-fg-muted">
          <span className="num text-fg">{events.length}</span> {t('events.eventsLabel')}
        </span>
      </header>
      {events.length === 0 ? (
        <p className="px-3 py-4 text-xs text-fg-muted">{t('events.empty')}</p>
      ) : (
        <ol className="overflow-y-auto px-3 py-2" data-testid="event-list">
          {events.map((event, i) => {
            const Icon = ICONS[event.kind] ?? FALLBACK_ICON;
            return (
              <li
                key={event.id}
                className="relative flex gap-3 pb-3 pl-1 last:pb-0"
              >
                <span
                  className="absolute left-3 top-4 bottom-0 w-px bg-rule"
                  aria-hidden
                  style={i === events.length - 1 ? { display: 'none' } : undefined}
                />
                <span className={`relative z-[1] mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-surface ring-1 ring-rule ${TONE[event.kind] ?? FALLBACK_TONE}`}>
                  <Icon size={11} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-xs text-fg">{describe(event, t)}</span>
                    {event.source && <span className="text-2xs text-fg-subtle">{t('common.via', { source: event.source })}</span>}
                  </div>
                  {event.note && event.kind !== 'delisted' && (
                    <p className="mt-0.5 text-2xs text-fg-muted">{event.note}</p>
                  )}
                  <span className="mt-0.5 block text-2xs text-fg-subtle">
                    {formatDateLong(event.ts)}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
